import { playerGlassStates } from '@lucimark/shared/helpers/playerGlassState'
import z from 'zod'

/** Current snapshot schema version. Append-only field evolution. */
export const PLAYER_DIAGNOSTICS_SNAPSHOT_SCHEMA_VERSION = 1 as const

/** Max UTF-8 bytes for a serialized snapshot on the wire (independent of heartbeat body cap). */
export const PLAYER_DIAGNOSTICS_SNAPSHOT_MAX_UTF8_BYTES = 16_384 as const

export const PLAYER_DIAGNOSTIC_FAULTS_MAX_LENGTH = 10 as const
export const PLAYER_DIAGNOSTIC_FAULT_CODE_MAX_LENGTH = 64 as const
export const PLAYER_DIAGNOSTIC_FRAME_ID_MAX_LENGTH = 512 as const

export const PLAYER_DIAGNOSTIC_FAULT_CODES = [
  'playback_timer_stuck',
  'frame_transition_stall',
  'frame_transition_accelerated',
  'scheduler_main_desync',
  'tick_cadence_degraded',
  'main_thread_ping_missed',
  'media_pipeline_error',
  'widget_mount_missing',
  'queue_duration_spine_mismatch',
  /** Queue ready but presentation surface still empty (brand cube / black) past stall budget. */
  'empty_playback_surface',
  /** Surface has frames expecting video, but no visible <video> paints (readyState / decode). */
  'video_paint_stall',
  /**
   * Painted surface keeps the same visible frame identity past its stall budget
   * (clock may still tick — len-1 rotating embed freeze / #1382).
   */
  'content_not_advancing',
  /**
   * Effective viewport, on-air layout canvas, and/or painted media box is vertical.
   * Always-on Sentry rising-edge; snapshot fields filled when diagnostic sampling is on.
   */
  'vertical_aspect_playback',
  /**
   * Monotonic presentation peak ≥ current plan length after rematch/shrink — poisons
   * last→head wrap guards until force-healed (#1662). Always-on + Sentry heal issue.
   */
  'stale_presentation_peak'
] as const

export const playerDiagnosticFaultCodeSchema = z.enum(PLAYER_DIAGNOSTIC_FAULT_CODES)
export type TPlayerDiagnosticFaultCode = z.infer<typeof playerDiagnosticFaultCodeSchema>

/** Lenient parse for readers (tolerates unknown codes from newer player builds). */
export const playerDiagnosticFaultCodeLenientSchema = z.string().min(1).max(PLAYER_DIAGNOSTIC_FAULT_CODE_MAX_LENGTH)

export const CRITICAL_PLAYER_DIAGNOSTIC_FAULT_CODES: ReadonlySet<TPlayerDiagnosticFaultCode> = new Set([
  'playback_timer_stuck',
  'frame_transition_stall',
  'scheduler_main_desync',
  'main_thread_ping_missed',
  'queue_duration_spine_mismatch',
  'empty_playback_surface',
  'video_paint_stall',
  'content_not_advancing',
  'vertical_aspect_playback',
  'stale_presentation_peak'
])

export const playerDiagnosticFaultSchema = z.object({
  code: playerDiagnosticFaultCodeLenientSchema,
  active: z.boolean(),
  sinceMs: z.number().int().positive().optional(),
  clearedAtMs: z.number().int().positive().optional(),
  detailMs: z.number().int().min(0).max(86_400_000).optional()
}).strict()

export type TPlayerDiagnosticFault = z.infer<typeof playerDiagnosticFaultSchema>

export const playerDiagnosticsSnapshotSchema = z.object({
  schemaVersion: z.literal(PLAYER_DIAGNOSTICS_SNAPSHOT_SCHEMA_VERSION),
  collectedAt: z.number().int().positive(),
  faults: z.array(playerDiagnosticFaultSchema).max(PLAYER_DIAGNOSTIC_FAULTS_MAX_LENGTH),
  visibleFrameId: z.string().min(1).max(PLAYER_DIAGNOSTIC_FRAME_ID_MAX_LENGTH).optional(),
  /**
   * Presentation / VBuffer plan index (paint authority). Prefer this when asking “what did UI last
   * accept?” — not necessarily the worker on-air slot.
   */
  notifiedIdx: z.number().int().min(-1).max(1_000_000).optional(),
  /**
   * PlaybackScheduler `lastNotifiedFrameIdx` (worker on-air index). Differs from {@link notifiedIdx}
   * when wrap/commit desyncs leave the surface parked while the scheduler advances (#1489).
   */
  workerNotifiedIdx: z.number().int().min(-1).max(1_000_000).optional(),
  frameElapsedMs: z.number().int().min(0).max(86_400_000).optional(),
  frameDurationMs: z.number().int().min(0).max(86_400_000).optional(),
  queueLength: z.number().int().min(0).max(10_000).optional(),
  /** Painted presentation-surface frame count (0 while brand cube / loading surface). */
  surfaceFrameCount: z.number().int().min(0).max(10_000).optional(),
  /** True when a surface commit is deferred waiting on the asset index. */
  pendingSurfaceCommit: z.boolean().optional(),
  /** How long the surface has been empty while the queue is non-empty (ms). */
  emptySurfaceStallMs: z.number().int().min(0).max(86_400_000).optional(),
  /** True when the on-air frame's main facet is video. */
  visibleFrameExpectsVideo: z.boolean().optional(),
  /** Count of <video> elements under the live-preview root. */
  visibleVideoElementCount: z.number().int().min(0).max(100).optional(),
  /** Best visible video HTMLMediaElement.readyState (0–4), if any. */
  visibleVideoReadyState: z.number().int().min(0).max(4).optional(),
  /** Best visible video videoWidth (0 when not decoded). */
  visibleVideoWidth: z.number().int().min(0).max(16_384).optional(),
  /** Best visible video networkState (0–3), if any. */
  visibleVideoNetworkState: z.number().int().min(0).max(3).optional(),
  /** MediaError.code on the best visible video (1–4), when set. */
  visibleVideoMediaErrorCode: z.number().int().min(1).max(4).optional(),
  /** Best visible video currentSrc/src scheme (local blob vs CDN vs HLS). */
  visibleVideoSrcScheme: z.enum(['blob', 'http', 'hls', 'other']).optional(),
  /** Continuous window where video is expected but no paint-ready video (ms). */
  videoPaintStallMs: z.number().int().min(0).max(86_400_000).optional(),
  /** Asset download progress 0–1 from AssetsService (omit when unknown / NaN). */
  assetDownloadProgress01: z.number().min(0).max(1).optional(),
  /**
   * Last gap in the main thread's 1s diagnostics poll, with the cause the worker thread witnessed.
   * Distinguishes a whole-process suspend (device asleep — painted dwell is not a stall) from a
   * blocked UI thread (a real stall) after a screen returns with a large dwell (#1500).
   */
  lastMainLoopGapMs: z.number().int().min(0).max(86_400_000).optional(),
  lastMainLoopGapAt: z.number().int().positive().optional(),
  lastMainLoopGapCause: z.enum(['process_suspend', 'main_thread_block', 'unknown']).optional(),
  lastFrameTransitionAt: z.number().int().positive().optional(),
  lastTickAt: z.number().int().positive().optional(),
  documentVisible: z.boolean().optional(),
  weeklyAvailabilitySuppressed: z.boolean().optional(),
  diagnosticSessionActiveUntil: z.number().int().positive().optional(),
  videoFatalCountSinceBoot: z.number().int().min(0).max(1_000_000).optional(),
  widgetRemountAttempts: z.number().int().min(0).max(1_000_000).optional(),
  /**
   * Device Owner crash/OOM self-heal restarts inside the native sliding window (issue #1369).
   * Rising count with a dark/flickering screen ⇒ crash loop, not a healthy reboot.
   */
  nativeSelfHealRestartCount: z.number().int().min(0).max(1_000_000).optional(),
  /** Epoch ms of the most recent self-heal relaunch in the window (0 / omit when none). */
  nativeSelfHealLastRestartAt: z.number().int().min(0).optional(),
  /**
   * WebView / page recovery reloads in this browser session (issue #1531 / #1532).
   * Rising count ⇒ hard self-heal loop; complements native Device Owner self-heal fields.
   */
  webRecoveryReloadCount: z.number().int().min(0).max(1_000_000).optional(),
  /** Epoch ms of the most recent web recovery reload (0 / omit when none). */
  webRecoveryReloadLastAt: z.number().int().min(0).optional(),
  /**
   * Playback aspect telemetry (append-only). Logical CSS viewport after rotation swap,
   * on-air layout canvas, and optional painted media bounding box. Used with
   * `vertical_aspect_playback` to diagnose unexpected portrait playout on landscape walls.
   */
  viewportAspect: z.enum(['horizontal', 'vertical', 'square']).optional(),
  effectiveViewportWidthPx: z.number().int().min(0).max(16_384).optional(),
  effectiveViewportHeightPx: z.number().int().min(0).max(16_384).optional(),
  screenRotation: z.enum(['0', '90', '180', '270']).optional(),
  onAirFrameLayoutAspect: z.enum(['horizontal', 'vertical', 'square']).optional(),
  onAirFrameLayoutWidthPx: z.number().int().min(0).max(16_384).optional(),
  onAirFrameLayoutHeightPx: z.number().int().min(0).max(16_384).optional(),
  visibleMediaAspect: z.enum(['horizontal', 'vertical', 'square']).optional(),
  visibleMediaWidthPx: z.number().int().min(0).max(16_384).optional(),
  visibleMediaHeightPx: z.number().int().min(0).max(16_384).optional(),
  aspectRestrictActive: z.boolean().optional(),
  /** Comma-joined `viewport` | `on_air_frame_layout` | `visible_media_rect` when vertical. */
  verticalAspectSignals: z.string().max(128).optional(),
  /**
   * Soft-deferred playlist swap / Feed vs lastSet identity (append-only).
   * Diagnoses Feed-ahead-of-materialization stalls without reading overlay UI.
   */
  feedPlaylistIdentity: z.string().min(1).max(PLAYER_DIAGNOSTIC_FRAME_ID_MAX_LENGTH).optional(),
  materializedPlaylistIdentity: z.string().min(1).max(PLAYER_DIAGNOSTIC_FRAME_ID_MAX_LENGTH).optional(),
  pendingPlaylistSwapPhase: z.enum(['downloading', 'ready_at_boundary', 'rematching']).optional(),
  pendingPlaylistSwapProgress01: z.number().min(0).max(1).optional(),
  pendingPlaylistSwapTitle: z.string().min(1).max(128).optional(),
  /**
   * Soft-defer / heal cutover trigger (live while rematching; sticky ~2min after success).
   * `boundary` = frame/hybrid EOF; `ready_timer` = ready remaining cap; `force_deadline` =
   * still downloading when unknown/absolute ceiling fired; `heal` = Feed-ahead rematerialize
   * (#1636) when soft-defer is idle.
   */
  pendingPlaylistSwapFlushReason: z.enum([
    'boundary',
    'ready_timer',
    'force_deadline',
    'heal'
  ]).optional(),
  /** Epoch ms when soft-defer armed (`downloading`). Sticky after success. */
  pendingPlaylistSwapArmedAtMs: z.number().int().min(0).optional(),
  /** Epoch ms when head reached `ready_at_boundary` (omit if force-cut while downloading). */
  pendingPlaylistSwapReadyAtMs: z.number().int().min(0).optional(),
  /** Epoch ms when rematch/apply started. */
  pendingPlaylistSwapFlushStartedAtMs: z.number().int().min(0).optional(),
  /** Epoch ms when rematch/apply finished (sticky sample after clear). */
  pendingPlaylistSwapFlushEndedAtMs: z.number().int().min(0).optional(),
  /** True when Feed/registration identity already matches next while lastSet/queue lags. */
  feedAheadOfMaterialization: z.boolean().optional(),
  /**
   * Coarse on-glass state. Same enum the player paints and Studio repeats.
   * Omitted when the device is on air normally or idle for a specific cause (geo, aspect, schedule).
   */
  glassState: z.enum(playerGlassStates).optional(),
  /** Editorial frames left out of the loop until their binary is cached. */
  skippedUncachedFrameCount: z.number().int().min(0).max(10_000).optional()
}).strict()

export type TPlayerDiagnosticsSnapshot = z.infer<typeof playerDiagnosticsSnapshotSchema>

export type TPlayerDiagnosticsSeverity = 'healthy' | 'degraded' | 'fault'

export function classifyPlayerDiagnosticsSeverity(
  snapshot: Pick<TPlayerDiagnosticsSnapshot, 'faults'> | null | undefined
): TPlayerDiagnosticsSeverity {
  if (snapshot == null || snapshot.faults.length === 0) {
    return 'healthy'
  }
  let hasActive = false
  let hasCritical = false
  let hasUnknown = false
  for (const fault of snapshot.faults) {
    if (!fault.active) {
      continue
    }
    hasActive = true
    const parsed = playerDiagnosticFaultCodeSchema.safeParse(fault.code)
    if (!parsed.success) {
      hasUnknown = true
      continue
    }
    if (CRITICAL_PLAYER_DIAGNOSTIC_FAULT_CODES.has(parsed.data)) {
      hasCritical = true
    }
  }
  if (!hasActive) {
    return 'healthy'
  }
  if (hasCritical) {
    return 'fault'
  }
  if (hasUnknown) {
    return 'degraded'
  }
  return 'degraded'
}

export function buildPlayerDiagnosticsHeartbeatFields(args: {
  snapshot: TPlayerDiagnosticsSnapshot | null
}): { playerDiagnostics?: TPlayerDiagnosticsSnapshot } {
  if (args.snapshot == null) {
    return {}
  }
  const serialized = JSON.stringify(args.snapshot)
  if (serialized.length > PLAYER_DIAGNOSTICS_SNAPSHOT_MAX_UTF8_BYTES) {
    return {}
  }
  const parsed = playerDiagnosticsSnapshotSchema.safeParse(args.snapshot)
  if (!parsed.success) {
    return {}
  }
  return { playerDiagnostics: parsed.data }
}

/**
 * After a web recovery reload the player may emit a slim snapshot that only carries
 * `webRecoveryReload*` (empty `faults`). Persisting that as-is would wipe Admin's view of
 * active stuck/fault codes. Merge previous **active** faults into that recovery-only snapshot.
 *
 * Do not use when `next.faults` is non-empty (including inactive clear edges) — that is intentional.
 */
/**
 * Snapshot merge hook used when persisting heartbeat diagnostics.
 *
 * Previously re-applied previous active faults onto recovery-only slim snapshots
 * (`faults: []` + `webRecoveryReloadCount > 0`). That resurrected `stuck` after a
 * WebView reload (new JS context, empty fault slots) until always-on detectors
 * sampled again. Recovery counters alone carry `hard_self_heal`; identity merge
 * keeps call sites stable without stitching prior-process faults.
 */
export function mergePlayerDiagnosticsPreservingActiveFaults(args: {
  prev: TPlayerDiagnosticsSnapshot | null | undefined
  next: TPlayerDiagnosticsSnapshot
}): TPlayerDiagnosticsSnapshot {
  void args.prev
  return args.next
}

export function diagnosticsSeverityFromStuckSuspected(suspected: boolean | null | undefined): TPlayerDiagnosticsSeverity {
  if (suspected === true) {
    return 'fault'
  }
  return 'healthy'
}

/** Player row fields used to render diagnostics health without an extra fetch. */
export interface TPlayerRowDiagnosticsInput {
  lastReportedPlayerDiagnostics?: TPlayerDiagnosticsSnapshot | null
  lastReportedPlayerDiagnosticsAt?: Date | string | number | null
  lastReportedPlaybackTimerStuckSuspected?: boolean | null
  lastReportedPlaybackTimerStuckOverrunMs?: number | null
  lastReportedPlaybackTimerStuckFrameId?: string | null
  lastReportedPlaybackTimerStuckVisibleDwellMs?: number | null
  lastReportedPlaybackTimerStuckTimingFlatlineMs?: number | null
  lastReportedPlaybackTimerStuckAt?: Date | string | number | null
}

function parsePlayerRowDiagnosticsTimestamp(raw: Date | string | number | null | undefined): Date | null {
  if (raw == null || raw === '') {
    return null
  }
  const parsed = raw instanceof Date ? raw : new Date(raw)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** Synthetic snapshot from PR1a stuck columns when no nested snapshot is stored yet. */
export function buildPlaybackTimerStuckDiagnosticsSnapshot(
  input: TPlayerRowDiagnosticsInput
): TPlayerDiagnosticsSnapshot | null {
  const suspected = input.lastReportedPlaybackTimerStuckSuspected
  if (suspected == null) {
    return null
  }
  const stuckAt = parsePlayerRowDiagnosticsTimestamp(input.lastReportedPlaybackTimerStuckAt)
  const collectedAt = stuckAt?.getTime() ?? Date.now()
  const faults: TPlayerDiagnosticFault[] = []
  if (suspected === true) {
    const fault: TPlayerDiagnosticFault = { code: 'playback_timer_stuck', active: true }
    if (stuckAt != null) {
      fault.sinceMs = stuckAt.getTime()
    }
    if (input.lastReportedPlaybackTimerStuckOverrunMs != null && input.lastReportedPlaybackTimerStuckOverrunMs > 0) {
      fault.detailMs = input.lastReportedPlaybackTimerStuckOverrunMs
    }
    faults.push(fault)
  }
  const snapshot: TPlayerDiagnosticsSnapshot = {
    schemaVersion: PLAYER_DIAGNOSTICS_SNAPSHOT_SCHEMA_VERSION,
    collectedAt,
    faults
  }
  if (input.lastReportedPlaybackTimerStuckFrameId != null && input.lastReportedPlaybackTimerStuckFrameId !== '') {
    snapshot.visibleFrameId = input.lastReportedPlaybackTimerStuckFrameId
  }
  if (input.lastReportedPlaybackTimerStuckVisibleDwellMs != null) {
    snapshot.frameElapsedMs = input.lastReportedPlaybackTimerStuckVisibleDwellMs
  }
  return snapshot
}

export function resolvePlayerRowDiagnosticsDisplay(input: TPlayerRowDiagnosticsInput): {
  snapshot: TPlayerDiagnosticsSnapshot | null
  severity: TPlayerDiagnosticsSeverity | null
  lastReportedAt: Date | null
} {
  const hasStuckSignal = input.lastReportedPlaybackTimerStuckSuspected != null
    || input.lastReportedPlaybackTimerStuckAt != null
  const snapshot = input.lastReportedPlayerDiagnostics
    ?? (hasStuckSignal ? buildPlaybackTimerStuckDiagnosticsSnapshot(input) : null)
  const lastReportedAt = parsePlayerRowDiagnosticsTimestamp(input.lastReportedPlayerDiagnosticsAt)
    ?? parsePlayerRowDiagnosticsTimestamp(input.lastReportedPlaybackTimerStuckAt)
  if (snapshot == null && lastReportedAt == null && !hasStuckSignal) {
    return { snapshot: null, severity: null, lastReportedAt: null }
  }
  const severity = snapshot != null
    ? classifyPlayerDiagnosticsSeverity(snapshot)
    : diagnosticsSeverityFromStuckSuspected(input.lastReportedPlaybackTimerStuckSuspected)
  return { snapshot, severity, lastReportedAt }
}
